/**
 * YouTube Service for fetching video transcripts and metadata
 * Uses Supabase Edge Function for transcription
 */

import { supabase, isSupabaseConfigured, getCurrentSession } from "@/lib/supabase";
import { sourcesService } from "./sourcesService";

interface VideoMetadata {
    title: string;
    description: string;
    channelName: string;
    publishedAt: string;
    thumbnailUrl: string;
}

interface TranscriptionResult {
    videoId: string;
    transcript: string;
    note?: string;
}

interface TranscriptionResponse {
    results: TranscriptionResult[];
}

export interface TranscriptItem {
    text: string;
    start: number;
    duration: number;
}

export interface VideoTranscriptResponse {
    videoId: string;
    transcript: TranscriptItem[];
}

export interface PlaylistVideo {
    videoId: string;
    title: string;
    thumbnail: string;
    transcript?: TranscriptItem[];
    transcriptError?: string;
}

export interface PlaylistTranscriptResponse {
    playlistId: string;
    total: number;
    videos: PlaylistVideo[];
}

class YouTubeService {
    /**
     * Extract video ID from various YouTube URL formats
     */
    extractVideoId(url: string): string | null {
        const patterns = [
            /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/)([a-zA-Z0-9_-]{11})/,
            /^([a-zA-Z0-9_-]{11})$/
        ];

        for (const pattern of patterns) {
            const match = url.match(pattern);
            if (match) {
                return match[1];
            }
        }
        return null;
    }

    /**
     * Extract playlist ID from YouTube URL
     */
    extractPlaylistId(url: string): string | null {
        const patterns = [
            /[?&]list=([a-zA-Z0-9_-]+)/,
            /^([a-zA-Z0-9_-]+)$/
        ];

        for (const pattern of patterns) {
            const match = url.match(pattern);
            if (match) {
                return match[1];
            }
        }
        return null;
    }

    /**
     * Check if URL is a playlist
     */
    isPlaylistUrl(url: string): boolean {
        return this.extractPlaylistId(url) !== null;
    }

    /**
     * Fetch all video IDs from a playlist
     */
    async fetchPlaylistVideos(playlistId: string): Promise<string[]> {
        const videoIds: string[] = [];
        
        try {
            // Use a proxy to fetch playlist data
            const playlistUrl = `https://www.youtube.com/playlist?list=${playlistId}`;
            const proxyUrl = `https://api.allorigins.win/raw?url=${encodeURIComponent(playlistUrl)}`;
            
            const response = await fetch(proxyUrl);
            if (!response.ok) {
                throw new Error("Failed to fetch playlist");
            }
            
            const html = await response.text();
            
            // Extract video IDs from the playlist page
            // YouTube embeds video IDs in the page data
            const videoIdPattern = /"videoId":"([a-zA-Z0-9_-]{11})"/g;
            const matches = html.matchAll(videoIdPattern);
            
            const seenIds = new Set<string>();
            for (const match of matches) {
                const videoId = match[1];
                if (!seenIds.has(videoId)) {
                    seenIds.add(videoId);
                    videoIds.push(videoId);
                }
            }
            
            if (videoIds.length === 0) {
                // Fallback: try to extract from ytInitialData
                const ytInitialDataMatch = html.match(/var ytInitialData = ({.+?});/);
                if (ytInitialDataMatch) {
                    try {
                        const data = JSON.parse(ytInitialDataMatch[1]);
                        const contents = data?.contents?.twoColumnBrowseResultsRenderer?.tabs?.[0]?.tabRenderer?.content?.sectionListRenderer?.contents?.[0]?.itemSectionRenderer?.contents?.[0]?.playlistVideoListRenderer?.contents;
                        
                        if (contents) {
                            for (const item of contents) {
                                const videoId = item?.playlistVideoRenderer?.videoId;
                                if (videoId && !seenIds.has(videoId)) {
                                    seenIds.add(videoId);
                                    videoIds.push(videoId);
                                }
                            }
                        }
                    } catch (e) {
                        console.error("Failed to parse ytInitialData", e);
                    }
                }
            }
            
            return videoIds;
        } catch (error: any) {
            console.error("Playlist fetch error:", error);
            throw new Error(`Failed to fetch playlist videos: ${error.message}`);
        }
    }

    /**
     * Import a playlist by transcribing all videos in it using the Edge Function
     */
    async importPlaylist(url: string, onProgress?: (current: number, total: number, videoId: string) => void, workspaceId?: string | null): Promise<{ 
        success: boolean; 
        imported: number; 
        failed: number; 
        errors?: string[];
    }> {
        if (!isSupabaseConfigured) {
            return { success: false, imported: 0, failed: 0, errors: ["Service not configured"] };
        }
        
        const session = await getCurrentSession();
        if (!session?.user) {
            return { success: false, imported: 0, failed: 0, errors: ["Please log in to import playlists"] };
        }

        // Validate that it's a playlist URL
        const playlistId = this.extractPlaylistId(url);
        if (!playlistId && !url.includes("playlist")) {
            return { success: false, imported: 0, failed: 0, errors: ["Invalid playlist URL"] };
        }

        try {
            // Use the Edge Function to transcribe the entire playlist
            // Pass URL directly if it's a full URL, otherwise pass the ID
            const transcriptionResponse = await this.transcribeYouTube(
                url.includes("http") ? { url } : { id: playlistId || url }
            );
            const results = transcriptionResponse.results;
            
            if (results.length === 0) {
                return { success: false, imported: 0, failed: 0, errors: ["No videos found in playlist"] };
            }

            let imported = 0;
            let failed = 0;
            const errors: string[] = [];

            // Process each transcribed video
            for (let i = 0; i < results.length; i++) {
                const result = results[i];
                const videoId = result.videoId;
                const videoUrl = `https://www.youtube.com/watch?v=${videoId}`;
                
                if (onProgress) {
                    onProgress(i + 1, results.length, videoId);
                }

                try {
                    // Check if video already exists
                    const { data: existingSource } = await supabase
                        .from("sources")
                        .select("id")
                        .eq("externalId", videoId)
                        .eq("user_id", session.user.id)
                        .single();

                    if (existingSource) {
                        failed++;
                        errors.push(`Video ${videoId}: Already imported`);
                        continue;
                    }

                    // Fetch metadata for the video
                    const metadata = await this.fetchVideoMetadata(videoId);
                    
                    const content = `# ${metadata.title}\n\n**Channel:** ${metadata.channelName}\n**Video ID:** ${videoId}\n\n## Transcript\n\n${result.transcript}`;

                    const sourceData = {
                        title: metadata.title,
                        type: "youtube" as const,
                        url: videoUrl,
                        content: content,
                        topics: [] as string[],
                        subTopics: [] as string[],
                        tags: [metadata.channelName] as string[],
                        linkedThreads: [] as string[]
                    };

                    await sourcesService.createSource(sourceData as any, workspaceId ?? null);
                    imported++;
                } catch (error: any) {
                    failed++;
                    errors.push(`Video ${videoId}: ${error.message}`);
                }
            }

            return { 
                success: imported > 0, 
                imported, 
                failed, 
                errors: errors.length > 0 ? errors : undefined 
            };
        } catch (error: any) {
            return { 
                success: false, 
                imported: 0, 
                failed: 0, 
                errors: [error.message || "Failed to import playlist"] 
            };
        }
    }

    /**
     * Fetch video metadata using oEmbed API (no API key required)
     */
    async fetchVideoMetadata(videoId: string): Promise<VideoMetadata> {
        const url = `https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}&format=json`;
        
        const response = await fetch(url);
        if (!response.ok) {
            throw new Error("Failed to fetch video metadata. Video may be private or unavailable.");
        }
        
        const data = await response.json();
        return {
            title: data.title || "Untitled Video",
            description: "",
            channelName: data.author_name || "Unknown Channel",
            publishedAt: new Date().toISOString(),
            thumbnailUrl: data.thumbnail_url || ""
        };
    }

    /**
     * Call Supabase Edge Function to transcribe YouTube video or playlist
     */
    async transcribeYouTube(input: { url?: string; id?: string }): Promise<TranscriptionResponse> {
        if (!isSupabaseConfigured) {
            throw new Error("Service not configured");
        }

        const session = await getCurrentSession();
        if (!session?.access_token) {
            throw new Error("Please log in to transcribe videos");
        }

        const edgeFunctionUrl = `https://tsnbktwryvnbmgsvzxdt.supabase.co/functions/v1/youtube-transcribe`;
        
        const response = await fetch(edgeFunctionUrl, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${session.access_token}`
            },
            body: JSON.stringify(input)
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Transcription failed: ${errorText || response.statusText}`);
        }

        const data: TranscriptionResponse = await response.json();
        
        if (!data.results || data.results.length === 0) {
            throw new Error("No transcription results returned");
        }

        return data;
    }

    /**
     * Fetch transcript for a single video
     */
    async fetchTranscript(videoId: string): Promise<string> {
        try {
            const response = await this.transcribeYouTube({ id: videoId });
            const result = response.results.find(r => r.videoId === videoId) || response.results[0];
            
            if (!result || !result.transcript) {
                throw new Error("No transcript found in response");
            }
            
            return result.transcript;
        } catch (error: any) {
            console.error("Transcript fetch failed:", error);
            throw new Error(error.message || "Unable to fetch transcript. The video may not have captions available, or captions may be disabled.");
        }
    }


    getRandomKey = (keys: any) => {
        const randomIndex = Math.floor(Math.random() * keys.length);
        return keys[randomIndex];
    };
    /**
     * Fetch transcript for a single video using Supadata API directly from client
     */
   /* async fetchVideoTranscriptFromAPI(videoId: string, lang: string = 'en'): Promise<VideoTranscriptResponse> {
        let apiKey = ["sd_9d74b81ba97d0c4a73d0c15d94621db8", "sd_6ec31357020c2d687f98ea0ce22d721a"];
        apiKey = this.getRandomKey(apiKey);
        if (!apiKey) {
            throw new Error('Supadata API key is not configured. Please set VITE_SUPADATA_API_KEY in your environment variables.');
        }

        const videoUrl = `https://www.youtube.com/watch?v=${videoId}`;
        const supadataUrl = 'https://api.supadata.ai/v1/transcript';
        
        try {
            const response = await fetch(supadataUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-api-key': apiKey,
                },
                body: JSON.stringify({
                    url: videoUrl,
                    lang: lang,
                }),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ error: response.statusText }));
                throw new Error(errorData.error || errorData.message || `Failed to fetch transcript: ${response.statusText}`);
            }

            const data = await response.json();
            
            // Handle async job response (if Supadata returns a job ID)
            if (data.jobId) {
                // Poll for job completion
                const jobResult = await this.pollSupadataJob(data.jobId, apiKey);
                return this.transformSupadataResponse(jobResult, videoId);
            }
            
            return this.transformSupadataResponse(data, videoId);
        } catch (error: any) {
            console.error('Supadata API error:', error);
            throw new Error(error.message || 'Failed to fetch transcript from Supadata');
        }
    }*/

    fetchVideoTranscriptFromAPI = async (videoUrl: any) => {
        let apiKey = ["sd_9d74b81ba97d0c4a73d0c15d94621db8", "sd_6ec31357020c2d687f98ea0ce22d721a"];
        apiKey = this.getRandomKey(apiKey);
        const endpoint = `https://api.supadata.ai/v1/youtube/transcript?url=${encodeURIComponent(videoUrl)}`;

        try {
            const response = await fetch(endpoint, {
                method: 'GET',
                headers: {
                    'x-api-key': apiKey,
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(`Error ${response.status}: ${errorData.message || 'Failed to fetch'}`);
            }

            const data = await response.json();

            // Supadata returns an object usually containing 'content' (full text)
            // and 'segments' (timestamped text)
            console.log("Full Transcript:", data.content);
            return data;

        } catch (error) {
            console.error("Fetch Error:", error.message);
        }
    };

    /**
     * Poll Supadata job status until completion
     */
    private async pollSupadataJob(jobId: string, apiKey: string, maxAttempts: number = 30): Promise<any> {
        const jobUrl = `https://api.supadata.ai/v1/transcript/${jobId}`;
        
        for (let attempt = 0; attempt < maxAttempts; attempt++) {
            await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2 seconds between polls
            
            const response = await fetch(jobUrl, {
                headers: {
                    'x-api-key': apiKey,
                },
            });

            if (!response.ok) {
                throw new Error(`Failed to check job status: ${response.statusText}`);
            }

            const jobData = await response.json();
            
            if (jobData.status === 'completed') {
                return jobData;
            } else if (jobData.status === 'failed') {
                throw new Error(jobData.error || 'Job failed');
            }
            // Continue polling if status is 'processing' or 'pending'
        }
        
        throw new Error('Job polling timeout - transcript processing took too long');
    }

    /**
     * Transform Supadata API response to our expected format
     */
    private transformSupadataResponse(data: any, videoId: string): VideoTranscriptResponse {
        let transcriptItems: TranscriptItem[] = [];
        
        // Handle different response formats from Supadata
        if (data.transcript && Array.isArray(data.transcript)) {
            // If transcript is already an array of items with timestamps
            transcriptItems = data.transcript.map((item: any) => ({
                text: item.text || item.content || String(item),
                start: item.start || item.startTime || item.timestamp || 0,
                duration: item.duration || (item.end ? item.end - (item.start || item.startTime || 0) : 3),
            }));
        } else if (data.text) {
            // If transcript is plain text, split into segments
            const sentences = data.text.split(/[.!?]\s+/).filter((s: string) => s.trim().length > 0);
            transcriptItems = sentences.map((sentence: string, index: number) => ({
                text: sentence.trim(),
                start: index * 5, // Estimate 5 seconds per sentence
                duration: 3,
            }));
        } else if (data.content) {
            // If transcript is in content field
            const sentences = data.content.split(/[.!?]\s+/).filter((s: string) => s.trim().length > 0);
            transcriptItems = sentences.map((sentence: string, index: number) => ({
                text: sentence.trim(),
                start: index * 5,
                duration: 3,
            }));
        } else if (data.segments && Array.isArray(data.segments)) {
            // If transcript is in segments format
            transcriptItems = data.segments.map((segment: any) => ({
                text: segment.text || segment.content || String(segment),
                start: segment.start || segment.startTime || 0,
                duration: segment.duration || (segment.end ? segment.end - (segment.start || 0) : 3),
            }));
        } else {
            throw new Error('Unexpected transcript format from Supadata API. Response: ' + JSON.stringify(data));
        }

        // Ensure timestamps are sequential
        let currentTime = 0;
        transcriptItems = transcriptItems.map(item => {
            if (item.start < currentTime) {
                item.start = currentTime;
            }
            currentTime = item.start + item.duration;
            return item;
        });

        if (transcriptItems.length === 0) {
            throw new Error('No transcript items found in Supadata response');
        }

        return {
            videoId: videoId,
            transcript: transcriptItems,
        };
    }

    /**
     * Fetch transcripts for all videos in a playlist using Supadata API directly from client
     */
    async fetchPlaylistTranscriptsFromAPI(playlistId: string, lang: string = 'en'): Promise<PlaylistTranscriptResponse> {
        let apiKey = ["sd_9d74b81ba97d0c4a73d0c15d94621db8", "sd_6ec31357020c2d687f98ea0ce22d721a"];
        apiKey = this.getRandomKey(apiKey);
        if (!apiKey) {
            throw new Error('Supadata API key is not configured. Please set VITE_SUPADATA_API_KEY in your environment variables.');
        }

        try {
            // First, fetch playlist videos using YouTube API or Supadata
            // For now, we'll use the existing method to get video IDs
            const videoIds = await this.fetchPlaylistVideos(playlistId);
            
            if (videoIds.length === 0) {
                throw new Error('No videos found in playlist');
            }

            // Fetch transcripts for each video using Supadata
            const videosWithTranscripts: PlaylistVideo[] = await Promise.all(
                videoIds.map(async (videoId) => {
                    try {
                        // Fetch metadata for the video
                        const metadata = await this.fetchVideoMetadata(videoId);
                        
                        // Fetch transcript using Supadata
                        const transcriptData = await this.fetchVideoTranscriptFromAPI(videoId, lang);
                        
                        return {
                            videoId: videoId,
                            title: metadata.title,
                            thumbnail: metadata.thumbnailUrl,
                            transcript: transcriptData.transcript,
                        };
                    } catch (error: any) {
                        // If transcript fails, still include the video but with error info
                        const metadata = await this.fetchVideoMetadata(videoId).catch(() => ({
                            title: 'Unknown Video',
                            thumbnailUrl: '',
                        }));
                        
                        return {
                            videoId: videoId,
                            title: metadata.title,
                            thumbnail: metadata.thumbnailUrl,
                            transcript: [],
                            transcriptError: error.message || 'Failed to fetch transcript',
                        };
                    }
                })
            );

            return {
                playlistId: playlistId,
                total: videoIds.length,
                videos: videosWithTranscripts,
            };
        } catch (error: any) {
            console.error('Playlist transcript fetch error:', error);
            throw new Error(error.message || 'Failed to fetch playlist transcripts from Supadata');
        }
    }

    /**
     * Import a YouTube video as a source document
     */
    async importVideo(url: string, workspaceId?: string | null): Promise<{ success: boolean; sourceId?: string; title?: string; error?: string }> {
        const videoId = this.extractVideoId(url);
        if (!videoId) {
            return { success: false, error: "Invalid YouTube URL" };
        }

        if (!isSupabaseConfigured) {
            return { success: false, error: "Service not configured" };
        }
        
        const session = await getCurrentSession();
        if (!session?.user) {
            return { success: false, error: "Please log in to import videos" };
        }

        const { data: existingSource } = await supabase
            .from("sources")
            .select("id")
            .eq("externalId", videoId)
            .eq("user_id", session.user.id)
            .single();

        if (existingSource) {
            return { success: false, error: "This video has already been imported" };
        }

        try {
            // Use the Edge Function to transcribe the video
            // Pass URL directly if it's a full URL, otherwise pass the ID
            const [metadata, transcriptionResponse] = await Promise.all([
                this.fetchVideoMetadata(videoId),
                this.transcribeYouTube(url.includes("http") ? { url } : { id: videoId })
            ]);

            const result = transcriptionResponse.results.find(r => r.videoId === videoId) || transcriptionResponse.results[0];
            
            if (!result || !result.transcript) {
                throw new Error("No transcript found in response");
            }

            const content = `# ${metadata.title}\n\n**Channel:** ${metadata.channelName}\n**Video ID:** ${videoId}\n\n## Transcript\n\n${result.transcript}`;

            const sourceData = {
                title: metadata.title,
                type: "youtube" as const,
                url: `https://www.youtube.com/watch?v=${videoId}`,
                content: content,
                topics: [] as string[],
                subTopics: [] as string[],
                tags: [metadata.channelName] as string[],
                linkedThreads: [] as string[]
            };

            const newSource = await sourcesService.createSource(sourceData as any, workspaceId ?? null);

            return {
                success: true,
                sourceId: newSource.id,
                title: metadata.title
            };

        } catch (error: any) {
            console.error("YouTube import error:", error);
            return { 
                success: false, 
                error: error.message || "Failed to import video"
            };
        }
    }
}

export const youtubeService = new YouTubeService();
